/**
 * The member form primitives.
 *
 * Extracted verbatim from CreateAccount so the public signup and the Admin CRM
 * "add member" drawer render the same inputs rather than two lookalikes that drift.
 * Nothing about the markup changed in the move — the signup tests drive these by
 * placeholder and role and still pass.
 */
import { useEffect, useMemo, useRef, useState } from "react";

export function Field({
  label, required, hint, children,
}: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "1.25rem" }}>
      <label style={{
        display: "block",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#64748B",
        marginBottom: "0.5rem",
      }}>
        {label}
        {required && <span style={{ color: "#E91E8C", marginLeft: "0.25rem" }}>*</span>}
        {hint && <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none", color: "#475569", marginLeft: "0.5rem", fontSize: "0.7rem" }}>({hint})</span>}
      </label>
      {children}
    </div>
  );
}

export const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "0.625rem",
  padding: "0.75rem 1rem",
  color: "white",
  fontFamily: "'Inter', sans-serif",
  fontSize: "0.9rem",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  boxSizing: "border-box",
};

export function TextInput({
  value, onChange, placeholder, type = "text", icon: Icon, autoComplete, required, invalid, onBlur,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  icon?: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  autoComplete?: string;
  required?: boolean;
  invalid?: boolean;
  /** Fired after the field loses focus, once the value has settled. */
  onBlur?: () => void;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {Icon && (
        <Icon size={15} style={{
          position: "absolute", left: "0.875rem", top: "50%", transform: "translateY(-50%)",
          color: focused ? "#E91E8C" : "#475569", transition: "color 0.2s",
        }} />
      )}
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur?.(); }}
        style={{
          ...inputStyle,
          paddingLeft: Icon ? "2.5rem" : "1rem",
          borderColor: focused ? "rgba(233,30,140,0.5)" : "rgba(255,255,255,0.1)",
          boxShadow: focused ? "0 0 0 3px rgba(233,30,140,0.1)" : "none",
        }}
      />
    </div>
  );
}

export function TextareaInput({ value, onChange, placeholder, rows = 4, required }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; required?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      required={required}
      aria-required={required || undefined}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...inputStyle,
        resize: "vertical",
        minHeight: "7rem",
        borderColor: focused ? "rgba(233,30,140,0.5)" : "rgba(255,255,255,0.1)",
        boxShadow: focused ? "0 0 0 3px rgba(233,30,140,0.1)" : "none",
      }}
    />
  );
}

export function SelectInput({ value, onChange, options, placeholder, required, invalid }: {
  value: string; onChange: (v: string) => void; options: string[]; placeholder?: string; required?: boolean; invalid?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...inputStyle,
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
          paddingRight: "2.5rem",
          borderColor: focused ? "rgba(233,30,140,0.5)" : "rgba(255,255,255,0.1)",
          boxShadow: focused ? "0 0 0 3px rgba(233,30,140,0.1)" : "none",
          color: value ? "white" : "#475569",
        }}
      >
        <option value="" disabled style={{ background: "#0D1220", color: "#64748B" }}>{placeholder || "Select…"}</option>
        {options.map((opt) => (
          <option key={opt} value={opt} style={{ background: "#0D1220", color: "white" }}>{opt}</option>
        ))}
      </select>
      <svg
        style={{ position: "absolute", right: "0.875rem", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#475569" }}
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}

/**
 * A searchable dropdown for long option lists (e.g. Industry).
 *
 * The list shows about 10 options at a time and scrolls for the rest. A filter
 * box at the top narrows the list as you type: "te" keeps options where any word
 * starts with those letters (Technology, Telecommunications), best matches first.
 */
export function FilterableSelectInput({ value, onChange, options, placeholder, invalid, searchPlaceholder, visibleCount = 10 }: {
  value: string; onChange: (v: string) => void; options: string[]; placeholder?: string; required?: boolean; invalid?: boolean;
  searchPlaceholder?: string; visibleCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const ROW_PX = 38;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    const starts: string[] = [];
    const wordStarts: string[] = [];
    for (const opt of options) {
      const lower = opt.toLowerCase();
      if (lower.startsWith(q)) starts.push(opt);
      else if (lower.split(/[^a-z0-9]+/).some(word => word.startsWith(q))) wordStarts.push(opt);
    }
    return [...starts, ...wordStarts];
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const idx = Math.max(0, options.indexOf(value));
    setActive(idx);
    requestAnimationFrame(() => {
      searchRef.current?.focus();
      listRef.current?.children[idx]?.scrollIntoView({ block: "nearest" });
    });
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const choose = (opt: string) => { onChange(opt); setOpen(false); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[active]) choose(filtered[active]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => { if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(true); } }}
        style={{
          ...inputStyle,
          textAlign: "left",
          cursor: "pointer",
          paddingRight: "2.5rem",
          borderColor: open ? "rgba(233,30,140,0.5)" : "rgba(255,255,255,0.1)",
          boxShadow: open ? "0 0 0 3px rgba(233,30,140,0.1)" : "none",
          color: value ? "white" : "#475569",
        }}
      >
        {value || placeholder || "Select…"}
      </button>
      <svg
        style={{ position: "absolute", right: "0.875rem", top: "1.35rem", transform: "translateY(-50%)", pointerEvents: "none", color: "#475569" }}
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
      {open && (
        <div
          style={{
            position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 50,
            background: "#0D1220", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "0.625rem",
            boxShadow: "0 12px 32px rgba(0,0,0,0.45)", overflow: "hidden",
          }}
        >
          <div style={{ padding: "0.5rem", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder || "Type to filter…"}
              aria-label="Filter options"
              aria-controls="filterable-select-list"
              aria-activedescendant={filtered[active] ? `fso-${active}` : undefined}
              style={{ ...inputStyle, padding: "0.5rem 0.75rem", fontSize: "0.85rem" }}
            />
          </div>
          <ul
            id="filterable-select-list"
            ref={listRef}
            role="listbox"
            style={{ listStyle: "none", margin: 0, padding: "0.25rem 0", maxHeight: `${ROW_PX * visibleCount}px`, overflowY: "auto" }}
          >
            {filtered.length === 0 && (
              <li style={{ padding: "0.6rem 1rem", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem" }}>No matches</li>
            )}
            {filtered.map((opt, i) => (
              <li
                key={opt}
                id={`fso-${i}`}
                role="option"
                aria-selected={opt === value}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                style={{
                  height: `${ROW_PX}px`, display: "flex", alignItems: "center", padding: "0 1rem", cursor: "pointer",
                  fontFamily: "'Inter', sans-serif", fontSize: "0.9rem",
                  color: opt === value ? "#F472B6" : "white",
                  background: i === active ? "rgba(233,30,140,0.15)" : "transparent",
                }}
              >
                {opt}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── INTEREST CHIP ─────────────────────────────────────────────────────────────

// ── STEP INDICATOR ────────────────────────────────────────────────────────────
